import { UserType } from '../../models/user.model';
import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { DataTableColumn, PaginatorStatus, SortConfiguration } from '../generics/data-table/data-table.component';
import { CustomTableFilter } from '../generics/table-filter/table-filter.component';
import { PageEvent } from '@angular/material/paginator';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorResponse } from 'src/app/models/error.model';
import { MatDialog } from '@angular/material/dialog';
import { UtilsService } from 'src/app/services/utils.service';
import { ContractsService } from 'src/app/services/contracts.service';
import { ContratoLocacionDTO } from 'src/app/models/location-contract';
import { BrokersService } from 'src/app/services/brokers.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ContractForGrid {
  contratoId: number;
  inquilino: string;
  propiedadDireccion: string;
  agenteNombre: string;
  agenteApellido: string;
  fechaFirma: string;
  fechaInicioAlquiler: string;
}

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss']
})
export class ContractsComponent implements OnInit {

  @ViewChild('buttonsColumnTemplate', { static: true })
  buttonsColumnTemplate!: TemplateRef<any>;

  paginatorStatus: PaginatorStatus = {
    itemsPerPage: 10,
    page: 0,
    totalElementCount: 0
  };

  columns: DataTableColumn[] = [
    {
      name: 'ID',
      parseValue: (value: ContratoLocacionDTO) => value.contratoId ? value.contratoId.toString() : 'N/A'
    },
    {
      name: 'Inquilino',
      parseValue: (value: ContratoLocacionDTO) => value.inquilinos?.[0]?.nombre ?? 'No especificado'
    },
    {
      name: 'Propiedad',
      parseValue: (value: ContratoLocacionDTO) => value.propiedad?.domicilio ?? 'No especificado'
    },
    {
      name: 'Fecha Firma',
      parseValue: (value: ContratoLocacionDTO) => value.fechaFirma ?? 'No especificado'
    },
    {
      name: 'Fecha Inicio',
      parseValue: (value: ContratoLocacionDTO) => value.fechaInicioAlquiler ?? 'No especificado'
    },
    { name: '', width: '130px', minWidth: '130px' }
  ];

  sortConfiguration?: SortConfiguration;
  contracts: ContratoLocacionDTO[] = [];
  filters: CustomTableFilter[] = [];
  filterValues: any = {};
  userType?: UserType;

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private contractsService: ContractsService,
    private brokersService: BrokersService
  ) { }

  ngOnInit(): void {
    this.columns.find(col => col.name === '')!.template = this.buttonsColumnTemplate;
    this.userType = this.authService.getUser()?.tipo;
    this.filters = this.buildFilters();
    this.getContracts();
  }

  private buildFilters(): CustomTableFilter[] {
    return [
      { key: 'id',                 description: 'ID',                             type: 'NUMBER' },
      { key: 'agente',             description: 'Agente',                         type: 'AUTOCOMPLETE',
        searchFn: (query: string) => forkJoin([
          this.brokersService.getBrokers(0, 20, [], { nombre: query }),
          this.brokersService.getBrokers(0, 20, [], { apellido: query })
        ]).pipe(map(([r1, r2]) => {
          const all = [...(r1.body ?? []), ...(r2.body ?? [])];
          const unique = all.filter((b, i, arr) => arr.findIndex(x => x.id === b.id) === i);
          return unique.map(b => ({ codigo: `${b.nombre} ${b.apellido}`, descripcion: `${b.nombre} ${b.apellido}` }));
        }))
      },
      { key: 'estado',             description: 'Estado',                         type: 'ENUM', availableOptions: [
        { codigo: 'BORRADOR',   descripcion: 'Borrador' },
        { codigo: 'ACTIVO',     descripcion: 'Activo' },
        { codigo: 'FINALIZADO', descripcion: 'Finalizado' },
      ]},
      { key: 'inquilino-nombre',   description: 'Nombre/Apellido del inquilino',   type: 'STRING' },
      { key: 'inquilino-dni',      description: 'DNI del inquilino',               type: 'DIGITS' },
      { key: 'propietario-nombre', description: 'Nombre/Apellido del propietario', type: 'STRING' },
      { key: 'propietario-dni',    description: 'DNI del propietario',             type: 'DIGITS' },
      { key: 'fecha-firma-desde',  description: 'Fecha de firma desde',            type: 'DATE' },
      { key: 'fecha-firma-hasta',  description: 'Fecha de firma hasta',            type: 'DATE' },
      { key: 'fecha-inicio-desde', description: 'Fecha de inicio desde',           type: 'DATE' },
      { key: 'fecha-inicio-hasta', description: 'Fecha de inicio hasta',           type: 'DATE' },
      { key: 'alquiler-desde',     description: 'Monto desde $',                  type: 'NUMBER' },
      { key: 'alquiler-hasta',     description: 'Monto hasta $',                  type: 'NUMBER' },
    ];
  }

  getContracts() {
    this.contractsService.getContratos(
      this.paginatorStatus.page,
      this.paginatorStatus.itemsPerPage,
      this.filterValues
    ).subscribe({
      next: (response) => {
        this.paginatorStatus.totalElementCount = parseInt(
          response.headers.get('pagination-total-count') ?? '0', 10);
        this.contracts = response.body ?? [];
      },
      error: (error: ErrorResponse) => {
        this.utilsService.alert('Error al obtener contratos', error.error?.descripcion ?? '');
        this.contracts = [];
      }
    });
  }

  updateContracts(event: PageEvent) {
    this.paginatorStatus.itemsPerPage = event.pageSize;
    this.paginatorStatus.page = event.pageIndex;
    this.getContracts();
  }

  updateSort(event: SortConfiguration) {
    this.sortConfiguration = event;
    this.getContracts();
  }

  deleteContract(contract: ContractForGrid) {
    console.log('Eliminar contrato', contract);
  }

  setFilterValues(values: any) {
    this.filterValues = values;
    this.getContracts();
  }

  createContractFromScratch() {
    console.log('Crear contrato desde 0');
  }

  createContractFromWarranty() {
    console.log('Crear contrato desde garantía');
  }
}
